import React from "react";
import { ColDef, ICellRendererParams } from "ag-grid-community";

interface BuildColumnOptions {
  savedWidths: { colId: string; width?: number }[];
}

type StyleMap = Record<string, string | number>;
type CustomValueStyleMap = Record<string, StyleMap>;
type BadgeFieldsMap = Record<string, CustomValueStyleMap>;

type ColumnsCellDef = {
  text?: string;
  field?: string;
  prefix?: string;
  suffix?: string;
  format?: string;
  style?: StyleMap;
};

function formatValue(raw: string, format: string): string {
  const num = parseFloat(raw);
  if (isNaN(num)) return raw;
  const dotIdx = format.indexOf(".");
  const decPlaces = dotIdx >= 0 ? format.length - dotIdx - 1 : 0;
  const intMinWidth = dotIdx >= 0 ? dotIdx : format.length;
  const sign = num < 0 ? "-" : "";
  const [intPart, decPart] = Math.abs(num).toFixed(decPlaces).split(".");
  const paddedInt = intPart.padStart(intMinWidth, " ");
  return `${sign}${paddedInt}${decPart !== undefined ? "." + decPart : ""}`;
}

type ColumnsRowDef = {
  cells: ColumnsCellDef[];
  style?: StyleMap;
};

type ColumnsTemplateDef = {
  gridColumns: string;
  columnGap?: string;
  rows: ColumnsRowDef[];
};

function applyTemplateWithNodes(
  template: string,
  data: Record<string, unknown>,
  valueLabelMap?: Record<string, string>,
  badgeFields?: BadgeFieldsMap,
): React.ReactNode | null {
  const keys = [...template.matchAll(/\$\{(\w+)\}/g)].map((m) => m[1]);
  if (keys.every((k) => data[k] == null)) return null;

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = /\$\{(\w+)\}/g;
  let match;
  let nodeKey = 0;

  while ((match = regex.exec(template)) !== null) {
    const before = template.slice(lastIndex, match.index);
    if (before) parts.push(before);

    const key = match[1];
    const raw = data[key] != null ? String(data[key]) : "";
    const display = valueLabelMap?.[raw] ?? raw;
    const badgeStyleMap = badgeFields?.[key];

    if (badgeStyleMap) {
      const base = badgeStyleMap["."];
      const override = badgeStyleMap[raw];
      parts.push(
        React.createElement(
          "span",
          { key: nodeKey++, style: base || override ? { ...base, ...override } : undefined },
          display,
        ),
      );
    } else {
      parts.push(display);
    }

    lastIndex = match.index + match[0].length;
  }

  const trailing = template.slice(lastIndex);
  if (trailing) parts.push(trailing);

  if (parts.length === 0) return null;
  if (parts.length === 1 && typeof parts[0] === "string") return parts[0];
  return React.createElement(React.Fragment, null, ...parts);
}

function buildColumnsTemplateRenderer(
  def: ColumnsTemplateDef,
  valueLabelMap?: Record<string, string>,
  badgeFields?: BadgeFieldsMap,
) {
  return (params: ICellRendererParams): React.ReactElement | string => {
    if (!params.data) return "";

    const cells: React.ReactNode[] = [];
    let keyIdx = 0;

    for (const row of def.rows) {
      // Skip row if all its field cells are null in data
      const fieldCells = row.cells.filter((c) => c.field != null);
      if (fieldCells.length > 0 && fieldCells.every((c) => params.data![c.field!] == null)) {
        continue;
      }

      for (const cell of row.cells) {
        const cellStyle: StyleMap = { ...row.style, ...cell.style };
        const hasStyle = Object.keys(cellStyle).length > 0;

        if (cell.text != null) {
          cells.push(
            React.createElement("span", { key: keyIdx++, style: hasStyle ? cellStyle : undefined }, cell.text),
          );
        } else if (cell.field != null) {
          const raw = params.data[cell.field] != null ? String(params.data[cell.field]) : "";
          const formattedRaw = cell.format ? formatValue(raw, cell.format) : raw;
          const display = valueLabelMap?.[raw] ?? formattedRaw;
          const prefix = cell.prefix ?? "";
          const suffix = cell.suffix ?? "";
          const badgeStyleMap = badgeFields?.[cell.field];

          if (badgeStyleMap) {
            const base = badgeStyleMap["."];
            const override = raw ? badgeStyleMap[raw] : undefined;
            const badgeStyle = base || override ? { ...base, ...override } : undefined;
            cells.push(
              React.createElement(
                "span",
                { key: keyIdx++, style: hasStyle ? cellStyle : undefined },
                React.createElement("span", { style: badgeStyle }, display),
              ),
            );
          } else {
            cells.push(
              React.createElement(
                "span",
                { key: keyIdx++, style: hasStyle ? cellStyle : undefined },
                prefix + display + suffix,
              ),
            );
          }
        } else {
          cells.push(React.createElement("span", { key: keyIdx++ }));
        }
      }
    }

    return React.createElement(
      "div",
      { className: "custom-value-cell" },
      React.createElement(
        "div",
        {
          style: {
            display: "grid",
            gridTemplateColumns: def.gridColumns,
            columnGap: def.columnGap ?? "6px",
            fontSize: "12px",
            width: "100%",
            rowGap: "3px",
          },
        },
        ...cells,
      ),
    );
  };
}

function buildCustomValueCellRenderer(
  styleMap: CustomValueStyleMap,
  subField?: string,
  subFieldStyle?: StyleMap,
  subFieldCustomValueStyle?: CustomValueStyleMap,
  primaryTemplate?: string,
  subFieldTemplate?: string,
  valueLabelMap?: Record<string, string>,
  badgeFields?: BadgeFieldsMap,
) {
  return (params: ICellRendererParams): React.ReactElement | string => {
    const rawValue = params.value != null ? String(params.value) : "";

    // Display content: template (with label map + inline badges) or raw value with label map
    const displayContent: React.ReactNode =
      primaryTemplate && params.data
        ? (applyTemplateWithNodes(primaryTemplate, params.data, valueLabelMap, badgeFields) ?? rawValue)
        : (valueLabelMap?.[rawValue] ?? rawValue);

    // Style key lookup always uses the raw field value
    const base = styleMap["."];
    const override = styleMap[rawValue];

    // Sub-field raw value: always from direct field, used for style lookup
    const subFieldRawValue =
      subField && params.data && params.data[subField] != null
        ? String(params.data[subField])
        : undefined;

    // Sub-field display content: template (with label map + badges) or direct field value with label map
    const subContent: React.ReactNode | null =
      subFieldTemplate && params.data
        ? applyTemplateWithNodes(subFieldTemplate, params.data, valueLabelMap, badgeFields)
        : subFieldRawValue != null
          ? (valueLabelMap?.[subFieldRawValue] ?? subFieldRawValue)
          : null;

    if (!base && !override && subContent == null) {
      if (typeof displayContent === "string") return displayContent;
      return React.createElement(React.Fragment, null, displayContent) as React.ReactElement;
    }

    const badge =
      base || override
        ? React.createElement(
            "span",
            { style: { ...base, ...override } },
            displayContent,
          )
        : displayContent;

    let resolvedSubStyle = subFieldStyle;
    if (subFieldCustomValueStyle && subFieldRawValue != null) {
      const subBase = subFieldCustomValueStyle["."];
      const subOverride = subFieldCustomValueStyle[subFieldRawValue];
      if (subBase || subOverride) {
        resolvedSubStyle = { ...subBase, ...subOverride };
      }
    }

    const sub =
      subContent != null && subContent !== ""
        ? React.createElement("span", { style: resolvedSubStyle }, subContent)
        : null;

    return React.createElement(
      "div",
      { className: "custom-value-cell" },
      badge,
      sub,
    );
  };
}

export function buildColumn(col: ColDef, opts: BuildColumnOptions): ColDef {
  const savedCol = opts.savedWidths.find((s) => s.colId === col.field);
  const hasSavedWidth = typeof savedCol?.width === "number";

  const customValueStyleMap = col.context?.["custom-value-style"] as
    | CustomValueStyleMap
    | undefined;
  const subField = col.context?.["sub-field"] as string | undefined;
  const subFieldStyle = col.context?.["sub-field-style"] as StyleMap | undefined;
  const subFieldCustomValueStyle = col.context?.["sub-field-custom-value-style"] as
    | CustomValueStyleMap
    | undefined;
  const primaryTemplate = col.context?.["primary-template"] as string | undefined;
  const subFieldTemplate = col.context?.["sub-field-template"] as string | undefined;
  const valueLabelMap = col.context?.["value-label-map"] as
    | Record<string, string>
    | undefined;
  const badgeFields = col.context?.["badge-fields"] as BadgeFieldsMap | undefined;
  const columnsTemplate = col.context?.["columns-template"] as ColumnsTemplateDef | undefined;

  const hasCustomRender =
    customValueStyleMap || subField || primaryTemplate || subFieldTemplate || badgeFields || columnsTemplate;

  return {
    ...col,
    ...(hasSavedWidth ? { width: savedCol!.width, flex: undefined } : {}),
    ...(hasCustomRender
      ? {
          cellRenderer: columnsTemplate
            ? buildColumnsTemplateRenderer(columnsTemplate, valueLabelMap, badgeFields)
            : buildCustomValueCellRenderer(
                customValueStyleMap ?? {},
                subField,
                subFieldStyle,
                subFieldCustomValueStyle,
                primaryTemplate,
                subFieldTemplate,
                valueLabelMap,
                badgeFields,
              ),
        }
      : {}),
    cellRendererSelector: (params: ICellRendererParams) => {
      if (params.value === undefined) {
        return { component: "SkeletonCell" };
      }
      if (params.value === null) {
        return { component: "NullValueCell" };
      }
      return undefined;
    },
  };
}
